/**
 * 問題の作業台から sandboxd を呼ぶ（problem-format.md §7、sandboxd-client.md §4〜§5、observability.md §5）。
 * どれも `class.run`（purpose = problem、`class.problem.id`、類題なら `class.problem.exercise`）の span の中で
 * 1 回だけ `/api/run` を送る。問題は Leitner に入れないので `class.card.grade` は作らない。
 */
import { judgeCases, ProtocolError, type JudgeCase, type JudgeResult } from '../../lib/judge';
import { byteLength, SandboxError, type RunResponse } from '../../lib/sandboxd';
import {
  recordRunOutcome,
  withSpan,
  type ClassAttributes,
  type RunOutcome,
  type Verdict,
} from '../../lib/telemetry';
import { sandbox } from '../../services';
import type { RunHooks } from '../cards/run';

/** どの問題・類題のコードか（テレメトリの属性に使う） */
export interface ProblemRunTarget {
  problemId: string;
  exerciseId?: string | undefined;
}

function outcomeOf(e: unknown): RunOutcome | null {
  if (!(e instanceof SandboxError)) return e instanceof ProtocolError ? 'http_error' : null;
  switch (e.kind) {
    case 'busy':
      return 'busy';
    case 'timeout':
      return 'gateway_timeout';
    case 'network':
      return 'network_error';
    case 'local-busy':
      return null;
    default:
      return 'http_error';
  }
}

async function runProblem<T>(
  target: ProblemRunTarget,
  code: string,
  stdins: string[],
  hooks: RunHooks,
  interpret: (res: RunResponse) => { value: T; verdict?: Verdict },
): Promise<T> {
  const attrs: ClassAttributes = {
    'class.run.purpose': 'problem',
    'class.run.cases': stdins.length,
    'class.run.code_bytes': byteLength(code),
    'class.problem.id': target.problemId,
  };
  if (target.exerciseId !== undefined) attrs['class.problem.exercise'] = target.exerciseId;
  return withSpan('class.run', attrs, async (span) => {
    let res: RunResponse;
    try {
      res = await sandbox.run(
        { code, stdins },
        {
          ...(hooks.signal ? { signal: hooks.signal } : {}),
          ...(hooks.onRetry ? { onRetry: hooks.onRetry } : {}),
        },
      );
    } catch (e) {
      const outcome = outcomeOf(e);
      if (outcome) recordRunOutcome(span, outcome);
      throw e;
    }
    try {
      const { value, verdict } = interpret(res);
      recordRunOutcome(span, 'completed', verdict);
      return value;
    } catch (e) {
      recordRunOutcome(span, 'http_error');
      throw e;
    }
  });
}

/** 「実行」: 標準入力 1 つで走らせる（採点しない） */
export function runProblemCode(
  target: ProblemRunTarget,
  code: string,
  stdin: string,
  hooks: RunHooks = {},
): Promise<RunResponse> {
  return runProblem(target, code, [stdin], hooks, (res) => {
    if (res.compile?.ok !== false && !res.runs?.[0])
      throw new ProtocolError('実行結果がありません');
    return { value: res };
  });
}

/** 「採点」: 全ケースを 1 回の `/api/run` の stdins で送り、judgeCases で判定する（judge.md §3） */
export function gradeProblemCode(
  target: ProblemRunTarget,
  code: string,
  cases: readonly JudgeCase[],
  hooks: RunHooks = {},
): Promise<JudgeResult> {
  return runProblem(
    target,
    code,
    cases.map((c) => c.in),
    hooks,
    (res) => {
      const judge = judgeCases(cases, res);
      return { value: judge, verdict: judge.overall };
    },
  );
}
